import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  Lock, Unlock, CheckCircle, AlertTriangle, Info,
  Search, Calendar, ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';

const PeriodManagement = () => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedPeriods, setSelectedPeriods] = useState(new Set());
  const [migrationMode, setMigrationMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 3; y <= currentYear + 1; y++) {
    years.push(y.toString());
  }

  useEffect(() => {
    loadPeriods();
    checkMigrationMode();
  }, [selectedYear]);

  const loadPeriods = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/historical/periods?year=${selectedYear}`);
      setPeriods(res.data);
    } catch (err) {
      toast.error('Failed to load periods');
    } finally {
      setLoading(false);
    }
  };

  const checkMigrationMode = async () => {
    try {
      const res = await api.get('/historical/migration-mode');
      setMigrationMode(res.data.migration_mode);
    } catch (err) {
      console.error('Error checking migration mode:', err);
    }
  };

  const togglePeriodSelection = (periodId) => {
    const newSelection = new Set(selectedPeriods);
    if (newSelection.has(periodId)) {
      newSelection.delete(periodId);
    } else {
      newSelection.add(periodId);
    }
    setSelectedPeriods(newSelection);
  };

  const selectAll = () => {
    const allIds = new Set(periods.map(p => p.id));
    setSelectedPeriods(allIds);
  };

  const clearSelection = () => {
    setSelectedPeriods(new Set());
  };

  const updatePeriodStatus = async (periodId, newStatus) => {
    setActionLoading(true);
    try {
      const res = await api.put(`/historical/periods/${periodId}`, { status: newStatus });
      toast.success(`Period ${res.data.year_month} set to ${newStatus}`);
      loadPeriods();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update period');
    } finally {
      setActionLoading(false);
    }
  };

  const bulkUpdatePeriods = async (newStatus) => {
    if (selectedPeriods.size === 0) {
      toast.warning('Please select at least one period');
      return;
    }

    setActionLoading(true);
    try {
      const res = await api.post('/historical/periods/bulk', {
        periods: Array.from(selectedPeriods),
        status: newStatus
      });
      toast.success(`${res.data.updated} periods updated to ${newStatus}`);
      setSelectedPeriods(new Set());
      loadPeriods();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleMigrationMode = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/historical/migration-mode', { enable: !migrationMode });
      setMigrationMode(res.data.migration_mode);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle migration mode');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">Open</span>;
      case 'closed':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Closed</span>;
      case 'locked':
        return <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">Locked</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const isCurrentMonth = (yearMonth) => {
    const now = new Date();
    const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return yearMonth === current;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Lock className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accounting Periods</h1>
            <p className="text-sm text-gray-500">Manage open, closed, and locked accounting periods</p>
          </div>
        </div>
        <button
          onClick={loadPeriods}
          className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
        >
          <RefreshCw size={14} className="mr-1" />
          Refresh
        </button>
      </div>

      {/* Migration Mode Banner */}
      <div className={`mb-6 p-4 rounded-xl border ${
        migrationMode 
          ? 'bg-amber-50 border-amber-200' 
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className={`w-5 h-5 ${migrationMode ? 'text-amber-600' : 'text-gray-400'}`} />
            <div>
              <h3 className={`font-medium ${migrationMode ? 'text-amber-800' : 'text-gray-700'}`}>
                Migration Mode: {migrationMode ? 'Enabled' : 'Disabled'}
              </h3>
              <p className={`text-sm ${migrationMode ? 'text-amber-600' : 'text-gray-500'}`}>
                {migrationMode 
                  ? 'Period locks are bypassed. All historical entries allowed. Disable when migration is complete.'
                  : 'Normal operation. Period restrictions apply.'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleMigrationMode}
            disabled={actionLoading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              migrationMode
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            } disabled:opacity-50`}
          >
            {actionLoading ? 'Processing...' : migrationMode ? 'Disable Migration Mode' : 'Enable Migration Mode'}
          </button>
        </div>
      </div>

      {/* Year Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Calendar size={16} className="text-gray-400" />
          <div className="flex space-x-1">
            {years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  selectedYear === year
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        {/* Bulk actions */}
        {selectedPeriods.size > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">{selectedPeriods.size} selected</span>
            <button
              onClick={clearSelection}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              onClick={() => bulkUpdatePeriods('open')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Open
            </button>
            <button
              onClick={() => bulkUpdatePeriods('closed')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              Close
            </button>
            <button
              onClick={() => bulkUpdatePeriods('locked')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Lock
            </button>
          </div>
        )}
      </div>

      {/* Periods Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedPeriods.size === periods.length && periods.length > 0}
                      onChange={() => selectedPeriods.size === periods.length ? clearSelection() : selectAll()}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {periods.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No periods found for {selectedYear}
                    </td>
                  </tr>
                ) : (
                  periods.map(period => (
                    <tr key={period.id} className={`hover:bg-gray-50 transition-colors ${
                      isCurrentMonth(period.year_month) ? 'bg-indigo-50' : ''
                    }`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedPeriods.has(period.id)}
                          onChange={() => togglePeriodSelection(period.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{period.year_month}</span>
                          {isCurrentMonth(period.year_month) && (
                            <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium">Current</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(period.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{new Date(period.period_start).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{new Date(period.period_end).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{period.notes || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {period.status !== 'open' && (
                            <button
                              onClick={() => updatePeriodStatus(period.id, 'open')}
                              disabled={actionLoading}
                              className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded"
                              title="Open period"
                            >
                              <Unlock size={14} />
                            </button>
                          )}
                          {period.status !== 'closed' && (
                            <button
                              onClick={() => updatePeriodStatus(period.id, 'closed')}
                              disabled={actionLoading}
                              className="px-2 py-1 text-xs text-yellow-600 hover:bg-yellow-50 rounded"
                              title="Close period"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                          {period.status !== 'locked' && (
                            <button
                              onClick={() => updatePeriodStatus(period.id, 'locked')}
                              disabled={actionLoading}
                              className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                              title="Lock period"
                            >
                              <Lock size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Unlock size={16} className="text-green-600" />
            <h3 className="font-medium text-green-800">Open</h3>
          </div>
          <p className="text-sm text-green-600">Transactions can be entered and modified. Normal operation.</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle size={16} className="text-yellow-600" />
            <h3 className="font-medium text-yellow-800">Closed</h3>
          </div>
          <p className="text-sm text-yellow-600">No new entries without admin override. Period is finalized.</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Lock size={16} className="text-red-600" />
            <h3 className="font-medium text-red-800">Locked</h3>
          </div>
          <p className="text-sm text-red-600">Fully locked. Only administrators can reopen after special approval.</p>
        </div>
      </div>
    </div>
  );
};

export default PeriodManagement;